/**
 * PaymentTokenizationService.js - Merchant Dashboard PCI DSS Compliant Tokenization
 * 
 * Mirrors customer portal tokenization service for consistent security
 * Never stores or transmits raw card data - only secure tokens
 * 
 * Key Features:
 * - Client-side tokenization before API calls
 * - PCI DSS Level 1 compliance
 * - Integration with backend tokenization service
 * - Comprehensive error handling and logging
 */

import apiClient from './api/apiClient';

class PaymentTokenizationService {
  constructor() {
    this.isInitialized = false;
    this.gatewayProvider = process.env.REACT_APP_PAYMENT_GATEWAY || 'tesouro';
  }

  /**
   * Initialize tokenization service
   * In production, this would initialize the payment gateway SDK
   */
  initialize() {
    if (this.isInitialized) return;
    
    console.log('PaymentTokenizationService initialized', {
      provider: this.gatewayProvider,
      environment: process.env.NODE_ENV,
      application: 'merchant-dashboard'
    });
    
    this.isInitialized = true;
  }

  /**
   * Tokenize payment method securely (PCI DSS Level 1 compliance)
   * Never stores actual card data - only creates secure tokens
   * 
   * @param {Object} cardData - Card information to tokenize
   * @returns {Object} Secure token and safe metadata
   */
  async tokenize(cardData) {
    this.initialize();
    
    try {
      // Critical: Validate no sensitive data is stored in this service
      this._validateTokenizationData(cardData);
      
      // In production, this would use actual gateway SDK (Stripe Elements, Square, etc.)
      // For now, we'll create a secure token via our backend tokenization service
      const tokenRequest = {
        // Only send safe data for tokenization
        method: 'card',
        last4: this._extractLast4(cardData.cardNumber),
        brand: this._detectCardBrand(cardData.cardNumber), // Use 'brand' consistently
        expiryMonth: cardData.expiryMonth,
        expiryYear: cardData.expiryYear,
        // Never include cardNumber or CVV in the request
      };
      
      // Call backend tokenization service (merchant endpoint, not portal)
      const response = await apiClient.post('/transactions/tokenize-payment', tokenRequest);
      
      const tokenResult = {
        success: true,
        token: response.data.token,
        method: 'card',
        last4: tokenRequest.last4,
        brand: tokenRequest.brand, // Use consistent field name
        expiryMonth: tokenRequest.expiryMonth,
        expiryYear: tokenRequest.expiryYear,
        // Never include sensitive data in response
      };
      
      console.log('Payment tokenization successful', {
        token_id: tokenResult.token,
        card_brand: tokenResult.brand,
        last4: tokenResult.last4,
        application: 'merchant-dashboard'
      });
      
      // Immediately clear any card data from memory
      this._clearSensitiveData(cardData);
      
      return tokenResult;
      
    } catch (error) {
      // Log error without sensitive data
      console.error('Payment tokenization failed', {
        error: error.message,
        provider: this.gatewayProvider,
        application: 'merchant-dashboard'
      });
      
      // Ensure sensitive data is cleared on error
      this._clearSensitiveData(cardData);
      
      throw new Error('Payment tokenization failed. Please try again.');
    }
  }

  /**
   * Validate that only safe data is being processed
   * @private
   */
  _validateTokenizationData(cardData) {
    if (!cardData) {
      throw new Error('Card data is required for tokenization');
    }

    if (!cardData.cardNumber || cardData.cardNumber.length < 13) {
      throw new Error('Valid card number is required');
    }

    if (!cardData.expiryMonth || !cardData.expiryYear) {
      throw new Error('Card expiration date is required');
    }

    if (!cardData.cvv || cardData.cvv.length < 3) {
      throw new Error('CVV is required');
    }
  }

  /**
   * Extract last 4 digits from card number
   * @private
   */
  _extractLast4(cardNumber) {
    const cleaned = cardNumber.replace(/\s/g, '');
    return cleaned.slice(-4);
  }

  /**
   * Detect card brand from card number BIN
   * @private
   */
  _detectCardBrand(cardNumber) {
    const cleaned = cardNumber.replace(/\s/g, '');
    
    // Card brand regex patterns (industry standard BIN ranges)
    const patterns = {
      VISA: /^4/,
      MASTERCARD: /^(5[1-5]|2[2-7])/,
      AMEX: /^3[47]/,
      DISCOVER: /^(6011|622(12[6-9]|1[3-9]\d|[2-8]\d{2}|9[0-2]\d|92[0-5])|64[4-9]|65)/,
      DINERS: /^(36|38|30[0-5])/,
      JCB: /^35(2[89]|[3-8]\d)/
    };
    
    for (const [brand, pattern] of Object.entries(patterns)) {
      if (pattern.test(cleaned)) {
        return brand;
      }
    }
    
    return 'UNKNOWN';
  }

  /**
   * Clear sensitive data from memory (PCI DSS requirement)
   * @private
   */
  _clearSensitiveData(cardData) {
    if (cardData) {
      // Overwrite sensitive fields in memory
      if (cardData.cardNumber) cardData.cardNumber = '';
      if (cardData.cvv) cardData.cvv = '';
      
      // Clear the object reference
      Object.keys(cardData).forEach(key => {
        if (key === 'cardNumber' || key === 'cvv') {
          delete cardData[key];
        }
      });
    }
  }

  /**
   * Validate stored token (for future use with recurring payments)
   * @param {string} token - Token to validate
   * @returns {boolean} Token validity
   */
  async validateToken(token) {
    try {
      const response = await apiClient.post('/transactions/validate-token', { token });
      return response.data.isValid;
    } catch (error) {
      console.error('Token validation failed:', error.message);
      return false;
    }
  }
}

const paymentTokenizationService = new PaymentTokenizationService();
export default paymentTokenizationService;